import React, { useEffect, useState } from "react";
import { getDaybook, saveDaybook, exportDaybookToExcel } from "../../components/Api/GmsDaybook";
import { getActivities } from "../../components/Api/GmsActivity";
import { getEmployees } from "../../components/Api/GmsEmployee";
import { getGroups } from "../../components/Api/GmsGroup";
import { getSpaServices } from "../../components/Api/GmsSpaService";
import { getActivityConfigurations } from "../../components/Api/GmsActivityConfiguration";
import Select from "react-select";
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, Card, CardHeader, CardBody, Container, Row, Col } from "reactstrap";
import Swal from "sweetalert2";
import { Download } from "lucide-react";
import 'bootstrap/dist/css/bootstrap.min.css';
import { 
  hasPagePermissionWithAdmin, 
  hasViewOnlyPermission, 
  hasEditOrHigherPermission, 
  hasDeletePermissionWithAdmin, 
  isUserAdmin 
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";

const defaultForm = {
  daybook_Id: 0,
  employee_Id: null,
  group_Id: null,
  service_Id: null,
  activity_Id: null,
  config_Id: null,
  transaction_DateTime: new Date().toISOString(),
  cost: 0,
  created_By: 1,
  created_Date: new Date().toISOString(),
};

const emptyFilter = {
  employee_Id: null,
  service_Id: null,
  booking_Id: null,
  fromDate: null,
  toDate: null,
};

// Initialize with null dates to get all records
const getInitialFilter = () => {
  return {
    ...emptyFilter,
    fromDate: null,
    toDate: null
  };
};

export default function DaybookPage() {
  const [daybooks, setDaybooks] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [activities, setActivities] = useState([]);
  const [groups, setGroups] = useState([]);
  const [services, setServices] = useState([]);
  const [filteredServices, setFilteredServices] = useState([]);
  const [activityConfigurations, setActivityConfigurations] = useState([]);
  const [allActivityConfigurations, setAllActivityConfigurations] = useState([]);
  const [form, setForm] = useState(defaultForm);
  const [filter, setFilter] = useState(getInitialFilter());
  
  const [showModal, setShowModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalCost, setTotalCost] = useState(0);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedTime, setSelectedTime] = useState({
    hours: new Date().getHours() > 12 ? new Date().getHours() - 12 : new Date().getHours() || 12,
    minutes: new Date().getMinutes(),
    ampm: new Date().getHours() >= 12 ? 'PM' : 'AM'
  });

  // Check permissions
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsDaybook');
  const isViewOnly = hasViewOnlyPermission(permissions, '/GmsDaybook');
  const hasEditOrHigher = hasEditOrHigherPermission(permissions, '/GmsDaybook');
  const hasDeletePermission = hasDeletePermissionWithAdmin(permissions, '/GmsDaybook');
  const isAdmin = isUserAdmin();
  
  // Get current user's employee ID (same as BookingPage)
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const currentEmployeeId = currentUser.user_EmployeeId || currentUser.employeeId;
  
  
  // Determine if employee filter should be shown
  const showEmployeeFilter = !isViewOnly || isAdmin || (isViewOnly && !currentEmployeeId);

  // Session management
  const getUserSession = () => {
    const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
    return {
      userId: authUser.userId || 1,
      userName: authUser.userName || "System",
    };
  };

  useEffect(() => {
    if (hasPermission) {
      loadDaybooks(filter, page);
      loadEmployees();
      loadActivities();
      loadGroups();
      loadServices();
      loadAllActivityConfigurations();
    } else {
      window.location.href = '/Dashboard';
    }
  }, [hasPermission, page]);



  const loadEmployees = async () => {
    try {
      const data = await getEmployees({});
      setEmployees(data);
    } catch (error) {
      console.error("Error loading employees:", error);
    }
  };

  const loadActivities = async () => {
    try {
      const data = await getActivities({});
      setActivities(data);
    } catch (error) {
      console.error("Error loading activities:", error);
    }
  };

  const loadGroups = async () => {
    try {
      const data = await getGroups({});
      setGroups(data);
    } catch (error) {
      console.error("Error loading groups:", error);
    }
  };

  const loadServices = async () => {
    try {
      const data = await getSpaServices({});
      setServices(data);
    } catch (error) {
      console.error("Error loading services:", error);
    }
  };

  const loadServicesByGroup = async (groupId) => {
    try {
      const filter = {
        group_Id: groupId,
        status: true
      };
      const configs = await getActivityConfigurations(filter);
      // Get unique service IDs from configurations
      const serviceIds = [...new Set(configs.map(config => config.service_Id))];
      // Filter services based on available configurations
      const availableServices = services.filter(service => serviceIds.includes(service.service_Id));
      return availableServices;
    } catch (error) {
      console.error("Error loading services by group:", error);
      return [];
    }
  };

  const loadActivityConfigurations = async (groupId, serviceId) => {
    try {
      const filter = {
        group_Id: groupId,
        service_Id: serviceId,
        status: true
      };
      const data = await getActivityConfigurations(filter);
      setActivityConfigurations(data);
    } catch (error) {
      console.error("Error loading activity configurations:", error);
    }
  };

  const loadAllActivityConfigurations = async () => {
    try {
      const filter = {
        status: true
      };
      const data = await getActivityConfigurations(filter);
      setAllActivityConfigurations(data);
    } catch (error) {
      console.error("Error loading all activity configurations:", error);
    }
  };

  const loadDaybooks = async (filterData = filter, pageNumber = page) => {
    setLoading(true);
    try {
      // Clean filter - remove null values and convert to proper types
      const cleanFilter = {
        ...(filterData.employee_Id !== null && filterData.employee_Id !== undefined && { employee_Id: filterData.employee_Id }),
        ...(filterData.service_Id !== null && filterData.service_Id !== undefined && { service_Id: filterData.service_Id }),
        ...(filterData.booking_Id !== null && filterData.booking_Id !== undefined && { booking_Id: filterData.booking_Id }),
        ...(filterData.fromDate && { fromDate: filterData.fromDate }),
        ...(filterData.toDate && { toDate: filterData.toDate })
      };
      
      // For users without edit permission, filter by their employee ID only (same as BookingPage)
      if (!hasEditOrHigher && currentEmployeeId) {
        cleanFilter.employee_Id = currentEmployeeId;
      }
      
      const data = await getDaybook(cleanFilter);
      // Sort by newest daybook_Id first (latest inserted record)
      const sortedData = data.sort((a, b) => b.daybook_Id - a.daybook_Id);
      const startIndex = (pageNumber - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      const paginatedData = sortedData.slice(startIndex, endIndex);
      setDaybooks(paginatedData);
      setTotalCount(sortedData.length);
      // Calculate total cost of all filtered records
      const totalCostValue = sortedData.reduce((total, daybook) => total + daybook.cost, 0);
      setTotalCost(totalCostValue);
      console.log("✅ Daybook data processed - Count:", sortedData.length, "Total Cost:", totalCostValue);
    } catch (error) {
      console.error("❌ Error loading daybooks:", error);
      Swal.fire("Error", "Failed to load daybooks: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (field, value) => {
    setForm({
      ...form,
      [field]: value,
    });
  };

  const handleEmployeeChange = (selectedOption) => {
    setForm({
      ...form,
      employee_Id: selectedOption ? selectedOption.value : null,
    });
  };

  const handleGroupChange = async (selectedOption) => {
    setForm({
      ...form,
      group_Id: selectedOption ? selectedOption.value : null,
      service_Id: null,
      activity_Id: null,
      cost: 0,
    });
    // Clear service and activity configurations when group changes
    setActivityConfigurations([]);
    
    if (selectedOption) {
      // Load services for the selected group
      const availableServices = await loadServicesByGroup(selectedOption.value);
      setFilteredServices(availableServices);
    } else {
      setFilteredServices([]);
    }
  };

  const handleServiceChange = (selectedOption) => {
    setForm({
      ...form,
      service_Id: selectedOption ? selectedOption.value : null,
      activity_Id: null,
      cost: 0,
    });
    // Load activity configurations when service is selected
    if (selectedOption && form.group_Id) {
      loadActivityConfigurations(form.group_Id, selectedOption.value);
    }
  };

  const handleActivityChange = (selectedOption) => {
    const selectedConfig = activityConfigurations.find(a => a.activity_Id === selectedOption?.value);
    setForm({
      ...form,
      activity_Id: selectedOption ? selectedOption.value : null,
      config_Id: selectedConfig ? selectedConfig.configId : null,
      cost: selectedConfig ? selectedConfig.rate : 0,
    });
  };

  const handleDateChange = (e) => {
    const newDate = e.target.value;
    setSelectedDate(newDate);
    updateDateTime(newDate, selectedTime);
  };

  const handleTimeChange = (field, value) => {
    const newTime = {
      ...selectedTime,
      [field]: value
    };
    setSelectedTime(newTime);
    updateDateTime(selectedDate, newTime);
  };

  const updateDateTime = (date = selectedDate, time = selectedTime) => {
    const dateToUse = date || new Date().toISOString().split('T')[0];
    let hours = time.hours;
    if (time.ampm === 'PM' && hours !== 12) {
      hours = hours + 12;
    }
    if (time.ampm === 'AM' && hours === 12) {
      hours = 0;
    }
    
    // Create datetime string without timezone conversion
    const paddedHours = hours.toString().padStart(2, '0');
    const paddedMinutes = time.minutes.toString().padStart(2, '0');
    const datetimeString = `${dateToUse}T${paddedHours}:${paddedMinutes}:00.000Z`;
    
    setForm(prevForm => ({
      ...prevForm,
      transaction_DateTime: datetimeString,
    }));
  };

  useEffect(() => {
    updateDateTime();
  }, []);

  // Handle cascading dropdowns
  useEffect(() => {
    if (form.group_Id && form.service_Id) {
      loadActivityConfigurations(form.group_Id, form.service_Id);
    }
  }, [form.group_Id, form.service_Id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.employee_Id || !form.group_Id || !form.service_Id || !form.activity_Id || !form.config_Id) {
      Swal.fire("Error", "Please select employee, group, service and activity", "error");
      return;
    }

    setSaveLoading(true);
    try {
      const session = getUserSession();
      const daybookData = {
        daybook_Id: form.daybook_Id,
        employee_Id: form.employee_Id,
        activity_Id: form.config_Id, // Use config_Id instead of activity_Id
        transaction_DateTime: form.transaction_DateTime,
        cost: form.cost,
        created_By: session.userId,
        created_Date: new Date().toISOString(),
      };
      
      const result = await saveDaybook(daybookData);
      Swal.fire("Success", result.message || "Daybook saved successfully", "success");
      
      // Reset only activity dropdown, keep other fields for next entry
      setForm({
        ...form,
        activity_Id: null,
        config_Id: null,
        cost: 0,
      });
      setActivityConfigurations([]);
      setFilteredServices([]);
      
      // Don't refresh here - only refresh when modal closes
    } catch (error) {
      Swal.fire("Error", "Failed to save daybook: " + error.message, "error");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilter({
      ...filter,
      [field]: value,
    });
  };

  const handleApplyFilter = () => {
    setPage(1);
    loadDaybooks(filter);
  };

  const handleClearFilter = () => {
    let newFilter = {
      ...emptyFilter,
      fromDate: null,
      toDate: null
    };
    
    setFilter(newFilter);
    setPage(1);
    loadDaybooks(newFilter);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleString('en-GB');
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleExportExcel = async () => {
    try {
      setLoading(true);
      // Clean filter - remove null values and convert to proper types
      const cleanFilter = {
        ...(filter.employee_Id !== null && filter.employee_Id !== undefined && { employee_Id: filter.employee_Id }),
        ...(filter.service_Id !== null && filter.service_Id !== undefined && { service_Id: filter.service_Id }),
        ...(filter.booking_Id !== null && filter.booking_Id !== undefined && { booking_Id: filter.booking_Id }),
        ...(filter.fromDate && { fromDate: filter.fromDate }),
        ...(filter.toDate && { toDate: filter.toDate })
      };
      
      // For users without edit permission, filter by their employee ID only
      if (!hasEditOrHigher && currentEmployeeId) {
        cleanFilter.employee_Id = currentEmployeeId;
      }
      
      await exportDaybookToExcel(cleanFilter);
      Swal.fire("Success", "Daybook report exported successfully", "success");
    } catch (error) {
      console.error("Error exporting daybook:", error);
      Swal.fire("Error", "Failed to export daybook report", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    setForm(defaultForm);
    setSelectedDate(new Date().toISOString().split('T')[0]);
    setSelectedTime({
      hours: new Date().getHours() > 12 ? new Date().getHours() - 12 : new Date().getHours() || 12,
      minutes: new Date().getMinutes(),
      ampm: new Date().getHours() >= 12 ? 'PM' : 'AM'
    });
    setActivityConfigurations([]);
    setFilteredServices([]);
    setShowModal(true);
  };

  const getEmployeeName = (employeeId) => {
    const employee = employees.find(e => e.employee_Id === employeeId);
    return employee ? employee.employee_NameEn : employeeId;
  };

  const getActivityName = (activityId) => {
    const activity = activities.find(a => a.activity_Id === activityId);
    return activity ? activity.activity_NameEn : activityId;
  };

  const getGroupName = (groupId) => {
    const group = groups.find(g => g.group_Id === groupId);
    return group ? group.group_Name : groupId;
  };

  const getServiceName = (serviceId) => {
    const service = services.find(s => s.spaService_Id === serviceId);
    return service ? service.spaServiceName_En : serviceId;
  };

  // Calculate total cost for current filtered data
  const calculateTotalCost = () => {
    return daybooks.reduce((total, daybook) => total + daybook.cost, 0);
  };

  if (!hasPermission) {
    return <div>Access Denied</div>;
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumb title="Daybook Management" breadcrumbItem="Spa" />
        
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h4 className="mb-0">Daybook Management</h4>
          <Button 
            color="primary" 
            onClick={handleExportExcel}
            disabled={loading || totalCount === 0}
          >
            <Download className="me-1" size={16} />
            Export Excel
          </Button>
        </div>

        {/* Filter Section */}
        <Card className="mb-4">
          <CardHeader>
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="card-title mb-0">Filter Daybook</h5>
              <Button
                color="primary"
                onClick={() => setShowFilters(!showFilters)}
              >
                <i className="bx bx-filter me-2"></i>
                {showFilters ? "Hide Filters" : "Show Filters"}
              </Button>
            </div>
          </CardHeader>
          {showFilters && (
            <CardBody>
              <Row className="g-3 mb-3">
                {showEmployeeFilter && (
                  <Col md={3}>
                    <label className="form-label">Employee</label>
                    <Select
                      value={employees.find(e => e.employee_Id === filter.employee_Id) 
                        ? { value: filter.employee_Id, label: employees.find(e => e.employee_Id === filter.employee_Id).employee_NameEn }
                        : null}
                      onChange={(option) => handleFilterChange('employee_Id', option ? option.value : null)}
                      options={employees.map(employee => ({
                        value: employee.employee_Id,
                        label: employee.employee_NameEn
                      }))}
                      isClearable
                      isSearchable
                      placeholder="Select Employee..."
                      menuPortalTarget={document.body}
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        control: (provided) => ({
                          ...provided,
                          backgroundColor: '#fff',
                          borderColor: '#ced4da',
                          color: '#495057'
                        }),
                        singleValue: (provided) => ({
                          ...provided,
                          color: '#495057'
                        }),
                        option: (provided, state) => ({
                          ...provided,
                          backgroundColor: state.isSelected ? '#007bff' : state.isFocused ? '#f8f9fa' : '#fff',
                          color: state.isSelected ? '#fff' : '#495057',
                          opacity: 1
                        }),
                        menu: (provided) => ({
                          ...provided,
                          zIndex: 9999
                        })
                      }}
                      maxMenuHeight={200}
                    />
                  </Col>
                )}
                <Col md={3}>
                  <label className="form-label">Service</label>
                  <Select
                    value={services.find(s => s.service_Id === filter.service_Id) 
                      ? { value: filter.service_Id, label: services.find(s => s.service_Id === filter.service_Id).service_NameEN }
                      : null}
                    onChange={(option) => handleFilterChange('service_Id', option ? option.value : null)}
                    options={services.map(service => ({
                      value: service.service_Id,
                      label: service.service_NameEN
                    }))}
                    isClearable
                    isSearchable
                    placeholder="Select Service..."
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      control: (provided) => ({
                        ...provided,
                        backgroundColor: '#fff',
                        borderColor: '#ced4da',
                        color: '#495057'
                      }),
                      singleValue: (provided) => ({
                        ...provided,
                        color: '#495057'
                      }),
                      option: (provided, state) => ({
                        ...provided,
                        backgroundColor: state.isSelected ? '#007bff' : state.isFocused ? '#f8f9fa' : '#fff',
                        color: state.isSelected ? '#fff' : '#495057',
                        opacity: 1
                      }),
                      menu: (provided) => ({
                        ...provided,
                        zIndex: 9999
                      })
                    }}
                    maxMenuHeight={200}
                  />
                </Col>
                <Col md={3}>
                  <label className="form-label">From Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={filter.fromDate || ''}
                    onChange={(e) => handleFilterChange('fromDate', e.target.value || null)}
                  />
                </Col>
                <Col md={3}>
                  <label className="form-label">To Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={filter.toDate || ''}
                    onChange={(e) => handleFilterChange('toDate', e.target.value || null)}
                  />
                </Col>
              </Row>
              <div className="d-flex justify-content-end">
                <Button 
                  color="primary" 
                  className="me-2" 
                  onClick={handleApplyFilter} 
                  disabled={loading}
                >
                  <i className="bx bx-search me-2"></i>
                  Apply Filter
                </Button>
                <Button 
                  color="warning" 
                  onClick={handleClearFilter}
                >
                  <i className="bx bx-refresh me-2"></i>
                  Clear Filter
                </Button>
              </div>
            </CardBody>
          )}
        </Card>

        {/* Table Section */}
        <Card>
          <CardHeader>
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="card-title mb-0">Daybook List ({totalCount})</h5>
              <div className="text-end">
                <div className="fw-bold text-success" style={{fontSize: '1.1rem'}}>
                  Total Cost: {totalCost.toFixed(2)}
                </div>
              </div>
            </div>
          </CardHeader>
          <CardBody>
            {loading ? (
              <div className="text-center py-4">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline">
                  <thead className="table-light">
                    <tr>
                      <th>#</th>
                      <th>Employee</th>
                      <th>Service</th>
                      <th>Transaction Date & Time</th>
                      <th>Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {daybooks.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="text-center">No daybook entries found</td>
                      </tr>
                    ) : (
                      daybooks.map((daybook, index) => {
                        // Get service name directly from daybook service_Id
                        const serviceName = getServiceName(daybook.service_Id);
                        
                        return (
                          <tr key={daybook.daybook_Id}>
                            <td>{(page - 1) * pageSize + index + 1}</td>
                            <td>{getEmployeeName(daybook.employee_Id)}</td>
                            <td>{serviceName}</td>
                            <td>{formatDateTime(daybook.transaction_DateTime)}</td>
                            <td>{daybook.cost.toFixed(2)}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            )}
            
            {/* Pagination */}
            <div className="d-flex justify-content-between align-items-center mt-4 p-3" 
                 style={{backgroundColor: '#f8f9fa', borderRadius: '0.375rem', border: '1px solid #dee2e6'}}>
              <div className="text-muted" style={{fontSize: '14px', fontWeight: '500'}}>
                <i className="mdi mdi-information-outline me-2" style={{color: '#556ee6'}}></i>
                Showing <span style={{color: '#556ee6', fontWeight: '600'}}>{totalCount > 0 ? (page - 1) * pageSize + 1 : 0}</span> to <span style={{color: '#556ee6', fontWeight: '600'}}>{Math.min(page * pageSize, totalCount)}</span> of <span style={{color: '#556ee6', fontWeight: '600'}}>{totalCount}</span> entries
              </div>
              {totalCount > pageSize && (
                <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                  <Button
                    color="light"
                    size="sm"
                    disabled={page === 1}
                    onClick={() => handlePageChange(page - 1)}
                    style={{
                      border: '1px solid #dee2e6',
                      borderRadius: '0.375rem',
                      minWidth: '32px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: page === 1 ? '#f8f9fa' : '#fff',
                      color: page === 1 ? '#6c757d' : '#495057'
                    }}
                  >
                    <i className="mdi mdi-chevron-left"></i>
                  </Button>
                  
                  {[...Array(Math.ceil(totalCount / pageSize))].map((_, index) => {
                    const pageNum = index + 1;
                    const isActive = page === pageNum;
                    
                    return (
                      <Button
                        key={pageNum}
                        color={isActive ? "primary" : "light"}
                        size="sm"
                        onClick={() => handlePageChange(pageNum)}
                        style={{
                          border: '1px solid #dee2e6',
                          borderRadius: '0.375rem',
                          minWidth: '32px',
                          height: '32px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: isActive ? '#556ee6' : '#fff',
                          borderColor: isActive ? '#556ee6' : '#dee2e6',
                          color: isActive ? '#fff' : '#495057',
                          fontWeight: isActive ? '600' : '400',
                          boxShadow: isActive ? '0 2px 4px rgba(85,110,230,0.25)' : 'none'
                        }}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                  
                  <Button
                    color="light"
                    size="sm"
                    disabled={page === Math.ceil(totalCount / pageSize)}
                    onClick={() => handlePageChange(page + 1)}
                    style={{
                      border: '1px solid #dee2e6',
                      borderRadius: '0.375rem',
                      minWidth: '32px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: page === Math.ceil(totalCount / pageSize) ? '#f8f9fa' : '#fff',
                      color: page === Math.ceil(totalCount / pageSize) ? '#6c757d' : '#495057'
                    }}
                  >
                    <i className="mdi mdi-chevron-right"></i>
                  </Button>
                </div>
              )}
            </div>
          </CardBody>
        </Card>

        {/* MANUAL ENTRY MODAL DISABLED - Daybook entries are now automatically created when services are marked complete */}
        {/* Add Transaction Modal */}
        {/* <Modal isOpen={showModal} toggle={() => {
          setShowModal(false);
          setPage(1); // Reset to page 1 to see new records
          loadDaybooks(filter); // Refresh data when modal closes
        }} size="lg">
          <ModalHeader toggle={() => {
            setShowModal(false);
            setPage(1); // Reset to page 1 to see new records
            loadDaybooks(filter); // Refresh data when modal closes
          }}>
            Add Daybook Transaction
          </ModalHeader>
          <form onSubmit={handleSubmit}>
            <ModalBody>
              <Row className="g-3">
                <Col md={6}>
                  <label className="form-label">Employee *</label>
                  <Select
                    value={employees.find(e => e.employee_Id === form.employee_Id) 
                      ? { value: form.employee_Id, label: employees.find(e => e.employee_Id === form.employee_Id).employee_NameEn }
                      : null}
                    onChange={handleEmployeeChange}
                    options={employees.map(employee => ({
                      value: employee.employee_Id,
                      label: employee.employee_NameEn
                    }))}
                    isClearable
                    placeholder="Select Employee..."
                    isSearchable
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      control: (provided) => ({
                        ...provided,
                        backgroundColor: '#fff',
                        borderColor: '#ced4da',
                        color: '#495057'
                      }),
                      singleValue: (provided) => ({
                        ...provided,
                        color: '#495057'
                      }),
                      option: (provided, state) => ({
                        ...provided,
                        backgroundColor: state.isSelected ? '#007bff' : state.isFocused ? '#f8f9fa' : '#fff',
                        color: state.isSelected ? '#fff' : '#495057',
                        opacity: 1
                      }),
                      menu: (provided) => ({
                        ...provided,
                        zIndex: 9999
                      })
                    }}
                    maxMenuHeight={200}
                  />
                </Col>
                <Col md={6}>
                  <label className="form-label">Date *</label>
                  <input
                    type="date"
                    className="form-control"
                    value={selectedDate}
                    onChange={handleDateChange}
                    required
                  />
                </Col>
                <Col md={4}>
                  <label className="form-label">Hours *</label>
                  <select
                    className="form-control"
                    value={selectedTime.hours}
                    onChange={(e) => handleTimeChange('hours', parseInt(e.target.value))}
                    required
                  >
                    {[...Array(12)].map((_, i) => (
                      <option key={i + 1} value={i + 1}>{i + 1}</option>
                    ))}
                  </select>
                </Col>
                <Col md={4}>
                  <label className="form-label">Minutes *</label>
                  <select
                    className="form-control"
                    value={selectedTime.minutes}
                    onChange={(e) => handleTimeChange('minutes', parseInt(e.target.value))}
                    required
                  >
                    {[...Array(60)].map((_, i) => (
                      <option key={i} value={i}>{i.toString().padStart(2, '0')}</option>
                    ))}
                  </select>
                </Col>
                <Col md={4}>
                  <label className="form-label">AM/PM *</label>
                  <select
                    className="form-control"
                    value={selectedTime.ampm}
                    onChange={(e) => handleTimeChange('ampm', e.target.value)}
                    required
                  >
                    <option value="AM">AM</option>
                    <option value="PM">PM</option>
                  </select>
                </Col>
                <Col md={4}>
                  <label className="form-label">Group *</label>
                  <Select
                    value={groups.find(g => g.group_Id === form.group_Id) 
                      ? { value: form.group_Id, label: groups.find(g => g.group_Id === form.group_Id).group_Name }
                      : null}
                    onChange={handleGroupChange}
                    options={groups.map(group => ({
                      value: group.group_Id,
                      label: group.group_Name
                    }))}
                    isClearable
                    placeholder="Select Group..."
                    isSearchable
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      control: (provided) => ({
                        ...provided,
                        backgroundColor: '#fff',
                        borderColor: '#ced4da',
                        color: '#495057'
                      }),
                      singleValue: (provided) => ({
                        ...provided,
                        color: '#495057'
                      }),
                      option: (provided, state) => ({
                        ...provided,
                        backgroundColor: state.isSelected ? '#007bff' : state.isFocused ? '#f8f9fa' : '#fff',
                        color: state.isSelected ? '#fff' : '#495057',
                        opacity: 1
                      }),
                      menu: (provided) => ({
                        ...provided,
                        zIndex: 9999
                      })
                    }}
                    maxMenuHeight={200}
                  />
                </Col>
                <Col md={4}>
                  <label className="form-label">Service *</label>
                  <Select
                    value={filteredServices.find(s => s.service_Id === form.service_Id) 
                      ? { value: form.service_Id, label: filteredServices.find(s => s.service_Id === form.service_Id).service_NameEN }
                      : null}
                    onChange={handleServiceChange}
                    options={filteredServices.map(service => ({
                      value: service.service_Id,
                      label: service.service_NameEN
                    }))}
                    isClearable
                    placeholder="Select Service..."
                    isSearchable
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      control: (provided) => ({
                        ...provided,
                        backgroundColor: '#fff',
                        borderColor: '#ced4da',
                        color: '#495057'
                      }),
                      singleValue: (provided) => ({
                        ...provided,
                        color: '#495057'
                      }),
                      option: (provided, state) => ({
                        ...provided,
                        backgroundColor: state.isSelected ? '#007bff' : state.isFocused ? '#f8f9fa' : '#fff',
                        color: state.isSelected ? '#fff' : '#495057',
                        opacity: 1
                      }),
                      menu: (provided) => ({
                        ...provided,
                        zIndex: 9999
                      })
                    }}
                    maxMenuHeight={200}
                  />
                </Col>
                <Col md={4}>
                  <label className="form-label">Activity *</label>
                  <Select
                    value={activityConfigurations.find(a => a.activity_Id === form.activity_Id) 
                      ? { value: form.activity_Id, label: activities.find(a => a.activity_Id === form.activity_Id)?.activity_NameEn || 'Unknown Activity' }
                      : null}
                    onChange={handleActivityChange}
                    options={activityConfigurations.map(config => {
                      const activity = activities.find(a => a.activity_Id === config.activity_Id);
                      return {
                        value: config.activity_Id,
                        label: activity ? activity.activity_NameEn : 'Unknown Activity'
                      };
                    })}
                    isClearable
                    placeholder="Select Activity..."
                    isSearchable
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      control: (provided) => ({
                        ...provided,
                        backgroundColor: '#fff',
                        borderColor: '#ced4da',
                        color: '#495057'
                      }),
                      singleValue: (provided) => ({
                        ...provided,
                        color: '#495057'
                      }),
                      option: (provided, state) => ({
                        ...provided,
                        backgroundColor: state.isSelected ? '#007bff' : state.isFocused ? '#f8f9fa' : '#fff',
                        color: state.isSelected ? '#fff' : '#495057',
                        opacity: 1
                      }),
                      menu: (provided) => ({
                        ...provided,
                        zIndex: 9999
                      })
                    }}
                    maxMenuHeight={200}
                  />
                </Col>
                <Col md={6}>
                  <label className="form-label">Cost</label>
                  <input
                    type="number"
                    step="0.01"
                    className="form-control"
                    value={form.cost}
                    onChange={(e) => handleFormChange('cost', parseFloat(e.target.value) || 0)}
                    readOnly
                  />
                </Col>
              </Row>
            </ModalBody>
            <ModalFooter>
              <Button 
                color="success" 
                type="submit" 
                disabled={saveLoading}
              >
                <i className="bx bx-check-circle me-2"></i>
                {saveLoading ? "Saving..." : "Save Transaction"}
              </Button>
              <Button 
                color="secondary" 
                onClick={() => {
                  setShowModal(false);
                  setPage(1); // Reset to page 1 to see new records
                  loadDaybooks(filter); // Refresh data when modal closes
                }}
              >
                <i className="bx bx-x-circle me-2"></i>
                Close
              </Button>
            </ModalFooter>
          </form>
        </Modal> */}
      </Container>
    </div>
  );
} 